import React, { Fragment, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import icon from '../../images/icon/magic.svg';
import gImg1 from '../../images/logo-fff.png';
import Seo from '../../components/Seo';
import { formatImageUrl, stripHtml } from '../../utils/security';

// Import only the default fallback image if needed
import gtu from '../../images/portfolio/gtu.jpg';

const PortfolioPage = () => {
    const { t, i18n } = useTranslation();
    const { lang } = useParams();
    const [filter, setFilter] = useState('all');
    const [dbProjects, setDbProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    // Set language based on URL parameter
    useEffect(() => {
        if (lang && (lang === 'en' || lang === 'ka')) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

    useEffect(() => {
        const fetchProjects = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_URL}portfolios.php?lang=${lang}`);
                const data = await response.json();
                if (Array.isArray(data)) {
                    setDbProjects(data);
                }
            } catch (error) {
                console.error('Error fetching portfolios from DB:', error);
            }
            setLoading(false);
        };
        fetchProjects();
    }, [lang, API_URL]);

    // Merge logic
    const projects = dbProjects.map(p => ({
        ...p,
        key: p.slug,
        title: stripHtml(p.title),
        summary: stripHtml(p.summary),
        image: formatImageUrl(p.image, gtu)
    }));

    const filteredProjects = filter === 'all' ? projects : projects.filter(p => p.category === filter);

    const [dbCategories, setDbCategories] = useState([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await fetch(`${API_URL}categories.php?type=portfolio`);
                const data = await res.json();
                if (Array.isArray(data) && data.length > 0) {
                    setDbCategories(data);
                }
            } catch (err) {
                console.error('Error fetching portfolio categories:', err);
            }
        };
        fetchCategories();
    }, [API_URL]);

    const categories = [
        { key: 'all', label: t('portfolio.all') || (lang === 'ka' ? 'ყველა' : 'All') },
        ...(dbCategories.length > 0
            ? dbCategories.map(c => ({
                key: c.slug,
                label: lang === 'ka' ? c.name_ka : (c.name_en || c.name_ka)
            }))
            : [
                { key: 'web', label: t('portfolio.web') },
                { key: 'elearning', label: t('portfolio.elearning') },
                { key: 'app', label: t('portfolio.app') },
                { key: 'ai_dev', label: t('portfolio.ai_dev') }
            ]
        )
    ];

    return (
        <Fragment>
            <Seo 
                title={t('seo.portfolio.title')}
                description={t('seo.portfolio.description')}
                keywords={t('seo.portfolio.keywords')}
                url={`/${lang}/portfolio`}
            />
            <Layout>

                {/* Modern Hero Section */}
                <motion.section
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="page-title pt-150 pos-rel bg_img"
                >
                    <div className="container">
                        <div className="page-title-wrap sd-title-wrap">
                            <div className="row mt-none-30 align-items-end">
                                <div className="col-lg-9 mt-30">
                                    <motion.div 
                                        initial={{ opacity: 0, x: -50 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3 }}
                                        className="page-title-box"
                                    >
                                        <motion.span 
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.5 }}
                                            className="sub-title"
                                        >
                                            <img src={icon} alt="" /> {t('navigation.portfolio') || t('portfolio.title')}
                                        </motion.span>
                                        <motion.h2 
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.7 }}
                                            className="title"
                                        >
                                            {t('portfolio.title')}
                                        </motion.h2>
                                        <p className="text-muted fs-5 mt-4 max-w-600">{t('portfolio.subtitle')}</p>
                                    </motion.div>
                                </div>
                                <div className="col-lg-3 mt-30 justify-content-center d-flex">
                                    <motion.div 
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.9, duration: 0.5 }}
                                        className="sd-right-img pos-rel d-none d-lg-block"
                                    >
                                        <img src={gImg1} alt="" />
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* Portfolio Section */}
                <section className="portfolio-section pt-85 cp-det-bg">
                    <div className="container">
                        {/* Dynamic Category Filter Pills */}
                        {categories.length > 0 && (
                            <div className="d-flex justify-content-center flex-wrap gap-2 mb-5">
                                {categories.map((cat) => (
                                    <button
                                        type="button"
                                        key={cat.key}
                                        className={`category-filter-btn ${filter === cat.key ? 'active' : ''}`}
                                        onClick={() => setFilter(cat.key)}
                                    >
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Grid */}
                        <motion.div layout className="portfolio-grid">
                            <div className="row">
                                {loading ? (
                                    <div className="col-12 text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <AnimatePresence mode="popLayout">
                                        {filteredProjects.map((project, index) => (
                                            <motion.div
                                                key={project.key}
                                                layout
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.8 }}
                                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                                className="col-lg-4 col-md-6 mb-30"
                                            >
                                            <Link to={`/${lang}/portfolio/${project.key}`} className="portfolio-link-wrapper">
                                                <motion.div
                                                    whileHover={{ y: -10 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="portfolio-item-modern"
                                                >
                                                    <div className="image-container">
                                                        <img 
                                                            src={project.image} 
                                                            alt={`${project.title || t(`portfolioItems.${project.key}`, project.key)} - ${t(`portfolio.${project.category}`, project.category)}`} 
                                                            loading="lazy"
                                                        />
                                                        <div className="overlay">
                                                            <span className="view-text">{t('portfolio.viewDetails')}</span>
                                                        </div>
                                                    </div>
                                                    <div className="portfolio-content">
                                                        <span className="category">{t(`portfolio.${project.category}`, project.category)}</span>
                                                        <h3 className="title">{project.title || t(`portfolioItems.${project.key}`, project.key)}</h3>
                                                    </div>
                                                </motion.div>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </section>

            </Layout>

            <style jsx>{`
                .category-filter-btn {
                    padding: 8px 24px;
                    border-radius: 30px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                .category-filter-btn:hover {
                    background: rgba(255, 255, 255, 0.1);
                    color: #fff;
                    transform: translateY(-2px);
                }
                .category-filter-btn.active {
                    background: #6366f1;
                    border-color: #6366f1;
                    color: #fff;
                    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
                }

                .portfolio-grid .row {
                    display: flex;
                    flex-wrap: wrap;
                }
                
                .portfolio-link-wrapper {
                    text-decoration: none;
                    display: block;
                    height: 100%;
                }

                .portfolio-item-modern {
                    background: rgb(8 11 37);
                    backdrop-filter: blur(15px);
                    -webkit-backdrop-filter: blur(15px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 24px;
                    padding: 20px;
                    height: 100%;
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    position: relative;
                }

                .portfolio-item-modern:hover {
                    background: rgba(255, 255, 255, 0.07);
                    border-color: rgba(70, 30, 180, 0.4);
                    transform: translateY(-10px) scale(1.02);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
                }

                .image-container {
                    width: 100%;
                    aspect-ratio: 16/10;
                    overflow: hidden;
                    border-radius: 16px;
                    margin-bottom: 20px;
                    position: relative;
                }

                .image-container img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.6s ease;
                }

                .overlay {
                    position: absolute;
                    top: 0; left: 0; width: 100%; height: 100%;
                    background: rgba(70, 30, 180, 0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                .portfolio-item-modern:hover .overlay {
                    opacity: 1;
                }

                .view-text {
                    color: white;
                    font-weight: 600;
                    font-size: 14px;
                    text-transform: uppercase;
                    letter-spacing: 2px;
                    border: 1px solid white;
                    padding: 8px 20px;
                    border-radius: 50px;
                }

                .portfolio-item-modern:hover .image-container img {
                    transform: scale(1.1);
                }

                .portfolio-content {
                    margin-top: auto;
                    text-align: left;
                }

                .portfolio-content .category {
                    font-size: 12px;
                    text-transform: uppercase;
                    color: #756e9e;
                    letter-spacing: 1px;
                    margin-bottom: 8px;
                    display: block;
                }

                .portfolio-content .title {
                    font-size: 1.2rem;
                    font-weight: 700;
                    margin-bottom: 5px;
                    color: #fff;
                    transition: color 0.3s ease;
                    line-height: 1.4;
                }

                .portfolio-item-modern:hover .title {
                    color: #461eb4;
                }
            `}</style>
        </Fragment>
    );
};

export default PortfolioPage;